import './bank.css';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fehlendeFunktion, upgradeAnbieten } from '../../billtoro/upgrade';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { darf, fehlerText, ohneRechtText, TarifMarke } from '../tarifHinweis';
import type { Overview } from '../types';

// Bank verbinden über Ponto (server/src/modules/rechnungstool-bank/): Die Firma meldet sich im Fenster von Ponto bei
// ihrer Bank an, danach kommen die Zahlungseingänge jeden Tag von selbst. „Jetzt abgleichen“ gibt sie als CSV an den
// vorhandenen Abgleich des Kontoauszugs (onAbgleich = read in Zahlungen.tsx): gleiche Vorschläge, gleiche Bestätigung.
// Ohne Ponto auf dem Server (PONTO_MODUS fehlt oder aus) und solange der Stand lädt: die Karte „Bank verknüpfen“ mit
// „bald“ und ihrem Fenster wie bisher (BankBald), in jedem Tarif. Verbinden, erneuern und trennen nur der Inhaber;
// abrufen und als gesehen markieren mit dem Recht „Ausgaben und Kasse“, abgleichen mit „Zahlungen“ (wie der Server).

// Tarif: wie der Server (rechnungstool-bank/routes.ts BANK_FEATURE)
const FEATURE = 'bank' as const;
// so viele Tage vor Ablauf die Karte ans Erneuern erinnert
const ERNEUERN_TAGE = 14;

export interface BankStand {
  verfuegbar: boolean;
  test: boolean;
  // verbinden, erneuern und trennen darf nur der Inhaber
  inhaber: boolean;
  verbunden: boolean;
  getrennt: boolean;
  verbundenAm: string | null;
  abgerufenAm: string | null;
  fehler: string | null;
  freigabeBis: string | null;
  konten: { iban: string; name: string | null; bank: string | null }[];
  neu: number;
}

const RUECKMELDUNG: Record<string, string> = {
  ok: 'Bank verbunden. Die Zahlungseingänge kommen jetzt jeden Tag von selbst.',
  erneuert: 'Freigabe erneuert.',
  abgebrochen: 'Bank verbinden abgebrochen.',
  fehler: 'Die Bank ließ sich gerade nicht verbinden. Bitte noch einmal versuchen.',
};

// Server ohne Ponto: die Karte von früher, „Bank verknüpfen“ erklärt im Fenster, wie es bis dahin geht
export function BankBald() {
  const [offen, setOffen] = useState(false);
  return (
    <>
      <div className="rt-up-card rt-up-bank">
        <h2>
          Bank verknüpfen <span className="badge b-none">bald</span>
        </h2>
        <p>Zahlungen kommen dann von selbst, ohne Hochladen.</p>
        <button type="button" className="btn" onClick={() => setOffen(true)}>
          Bank verknüpfen
        </button>
      </div>
      <Dialog open={offen} onClose={() => setOffen(false)} title="Bank verknüpfen">
        <p>
          Das direkte Verknüpfen mit Ihrer Bank wird später freigeschaltet. Bis dahin: Umsätze im Online-Banking als CSV
          oder Excel exportieren und hier hochladen.
        </p>
        <div className="dlg-actions">
          <button type="button" className="btn primary" onClick={() => setOffen(false)}>
            Verstanden
          </button>
        </div>
      </Dialog>
    </>
  );
}

const zeit = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export function BankKarte({ data, busy, onAbgleich }: { data: Overview; busy: boolean; onAbgleich(file: File): Promise<void> }) {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const res = useApi<BankStand>(`/api/rechnungstool/${data.firm.id}/bank`);
  const [laeuft, setLaeuft] = useState<'verbinden' | 'abrufen' | 'abgleichen' | 'erneuern' | 'trennen' | null>(null);
  const [trennen, setTrennen] = useState(false);
  const fehlt = fehlendeFunktion(data.tarif, FEATURE);
  // Rechte im Team (nur Anzeige, der Server prüft)
  const zahlen = darf(data.tarif, 'zahlungen');
  const ausgaben = darf(data.tarif, 'ausgaben');
  const s = res.data;

  // zurück aus dem Fenster von Ponto
  const rueck = params.get('bank');
  useEffect(() => {
    if (!rueck) return;
    if (RUECKMELDUNG[rueck]) toast(RUECKMELDUNG[rueck]);
    const p = new URLSearchParams(params);
    p.delete('bank');
    setParams(p, { replace: true });
    // nach dem Erneuern die neue Frist holen
    if (rueck === 'erneuert') void api.post(`/api/rechnungstool/${data.firm.id}/bank/abrufen`, {}).catch(() => undefined).then(() => res.reload());
  }, [rueck]); // eslint-disable-line react-hooks/exhaustive-deps

  async function weiter(pfad: 'verbinden' | 'erneuern') {
    if (fehlt && pfad === 'verbinden') {
      upgradeAnbieten(data.tarif, FEATURE);
      return;
    }
    setLaeuft(pfad);
    try {
      const r = await api.post<{ url: string }>(`/api/rechnungstool/${data.firm.id}/bank/${pfad}`, {});
      window.location.assign(r.url);
    } catch (err) {
      toast(fehlerText(err) ?? errorText(err));
      setLaeuft(null);
    }
  }

  async function abrufen() {
    setLaeuft('abrufen');
    try {
      const r = await api.post<{ neu: number }>(`/api/rechnungstool/${data.firm.id}/bank/abrufen`, {});
      toast(r.neu ? (r.neu === 1 ? '1 neuer Zahlungseingang' : `${r.neu} neue Zahlungseingänge`) : 'Keine neuen Zahlungseingänge');
    } catch (err) {
      toast(fehlerText(err) ?? errorText(err));
    } finally {
      await res.reload();
      setLaeuft(null);
    }
  }

  async function abgleichen(alle: boolean) {
    setLaeuft('abgleichen');
    try {
      const r = await api.get<{ anzahl: number; ids: string[]; csv: string }>(`/api/rechnungstool/${data.firm.id}/bank/eingaenge${alle ? '?alle=1' : ''}`);
      if (!r.anzahl) {
        toast('Keine Zahlungseingänge zum Abgleichen');
        return;
      }
      await onAbgleich(new File([r.csv], 'Bank.csv', { type: 'text/csv' }));
      // als gesehen markieren nur mit dem Recht „Ausgaben und Kasse“ (sonst zählen sie weiter als neu)
      if (ausgaben) await api.post(`/api/rechnungstool/${data.firm.id}/bank/gesehen`, { ids: r.ids });
      await res.reload();
    } catch (err) {
      toast(fehlerText(err) ?? errorText(err));
    } finally {
      setLaeuft(null);
    }
  }

  async function trennenJetzt() {
    setLaeuft('trennen');
    try {
      await api.del(`/api/rechnungstool/${data.firm.id}/bank`);
      toast('Bank getrennt');
      setTrennen(false);
      await res.reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setLaeuft(null);
    }
  }

  // noch nicht freigeschaltet (Server ohne Ponto) oder noch nicht geladen: wie bisher „bald“
  if (!s || !s.verfuegbar) return <BankBald />;

  if (!s.verbunden) {
    return (
      <div className="rt-up-card rt-up-bank bk-karte">
        <h2>
          Bank verbinden
          <TarifMarke tarif={data.tarif} fehlt={fehlt} />
          {s.test && <span className="badge b-warn">Test</span>}
        </h2>
        {s.getrennt ? (
          <p className="bk-fehler">{s.fehler ?? 'Die Verbindung zur Bank ist beendet. Bitte neu verbinden.'}</p>
        ) : (
          <p>Zahlungseingänge kommen jeden Tag von selbst, ohne Hochladen. Sie melden sich dafür einmal bei Ihrer Bank an.</p>
        )}
        {s.inhaber ? (
          <button type="button" className="btn primary" disabled={!!laeuft || fehlt === 'bald'} onClick={() => void weiter('verbinden')}>
            {laeuft === 'verbinden' ? 'Öffnet …' : s.getrennt ? 'Neu verbinden' : 'Bank verbinden'}
          </button>
        ) : (
          <p className="rt-hint">Verbinden kann nur der Inhaber der Firma.</p>
        )}
        <p className="bk-klein">
          Über Ponto, einen von der Bankenaufsicht zugelassenen Dienst. Billtoro sieht nur Ihre Umsätze, nie Ihre PIN oder
          TAN.
        </p>
      </div>
    );
  }

  const tage = s.freigabeBis ? Math.ceil((new Date(s.freigabeBis).getTime() - Date.now()) / 86_400_000) : null;
  const bald = tage !== null && tage <= ERNEUERN_TAGE;
  return (
    <div className="rt-up-card bk-karte">
      <h2>
        Bank verbunden
        {s.test && <span className="badge b-warn">Test</span>}
      </h2>
      <ul className="bk-konten">
        {s.konten.map((k) => (
          <li key={k.iban}>
            <b>{k.bank ?? k.name ?? 'Konto'}</b> <span>{k.iban}</span>
          </li>
        ))}
      </ul>
      <p className="bk-klein">
        {s.abgerufenAm ? `Zuletzt abgerufen ${zeit(s.abgerufenAm)}` : 'Noch nicht abgerufen'}
        {s.freigabeBis && !bald ? ` · Freigabe bis ${fmtDate(s.freigabeBis.slice(0, 10))}` : ''}
      </p>
      {s.fehler && <p className="bk-fehler">{s.fehler}</p>}
      {bald && (
        <div className="notice warn bk-erneuern">
          {tage! > 0 ? `Die Freigabe Ihrer Bank läuft in ${tage} ${tage === 1 ? 'Tag' : 'Tagen'} ab.` : 'Die Freigabe Ihrer Bank ist abgelaufen.'}{' '}
          {s.inhaber ? (
            <button type="button" className="linkbtn" disabled={!!laeuft} onClick={() => void weiter('erneuern')}>
              Freigabe erneuern
            </button>
          ) : (
            'Erneuern kann nur der Inhaber.'
          )}
        </div>
      )}
      {zahlen ? (
        <button type="button" className="btn primary" disabled={!!laeuft || busy || !s.neu} onClick={() => void abgleichen(false)}>
          {laeuft === 'abgleichen' || busy
            ? 'Wird abgeglichen …'
            : s.neu
              ? `${s.neu} ${s.neu === 1 ? 'neuen Eingang' : 'neue Eingänge'} abgleichen`
              : 'Keine neuen Eingänge'}
        </button>
      ) : (
        <p className="rt-hint">{ohneRechtText('zahlungen')}</p>
      )}
      <div className="bk-mehr">
        {ausgaben && (
          <button type="button" className="linkbtn" disabled={!!laeuft} onClick={() => void abrufen()}>
            {laeuft === 'abrufen' ? 'Ruft ab …' : 'Jetzt abrufen'}
          </button>
        )}
        {zahlen && (
          <button type="button" className="linkbtn" disabled={!!laeuft || busy} onClick={() => void abgleichen(true)}>
            Letzte 30 Tage abgleichen
          </button>
        )}
        {s.inhaber && (
          <button type="button" className="linkbtn" disabled={!!laeuft} onClick={() => setTrennen(true)}>
            Trennen
          </button>
        )}
      </div>
      <Dialog open={trennen} onClose={() => setTrennen(false)} title="Bank trennen">
        <p>Billtoro holt dann keine Zahlungseingänge mehr von Ihrer Bank. Eingetragene Zahlungen bleiben bei den Rechnungen.</p>
        <div className="dlg-actions">
          <button type="button" className="btn danger" disabled={laeuft === 'trennen'} onClick={() => void trennenJetzt()}>
            {laeuft === 'trennen' ? 'Trennt …' : 'Bank trennen'}
          </button>
          <button type="button" className="btn" onClick={() => setTrennen(false)}>
            Abbrechen
          </button>
        </div>
      </Dialog>
    </div>
  );
}
